// src/components/diary/DiarySettings.jsx
import React, { useState, useEffect } from 'react';
import { 
  Calendar, Clock, Users, BookOpen, Settings, Palette, Globe, 
  Shield, Layout, Bell, Sparkles, Save, X, Loader2, Upload
} from 'lucide-react';
import { SettingsModal } from '../settings/shared/SettingsModal';
import { TabNavigation } from '../settings/shared/TabNavigation';
import { BasicInfoTab } from '../settings/shared/BasicInfoTab';
import { DesignBrandingSection } from '../settings/shared/DesignSection';
import { HeaderSettings } from '../settings/shared/HeaderSettings';
import { PrivacySection } from '../settings/shared/PrivacySection';
import { SEOSection } from '../settings/shared/SEOSection';
import { firestoreService } from '../../services/firestoreService';

const DIARY_FEATURES = {
  meetings: { label: 'Meetings & Calendar', icon: Calendar, description: 'Schedule and manage meetings' },
  journal: { label: 'Journal Entries', icon: BookOpen, description: 'Personal journal and notes' },
  contacts: { label: 'Contacts', icon: Users, description: 'Contact management' },
  notes: { label: 'Quick Notes', icon: BookOpen, description: 'Meeting notes and ideas' },
  templates: { label: 'Meeting Templates', icon: Layout, description: 'Reusable meeting templates' },
  aiAssist: { label: 'AI Assistant', icon: Sparkles, description: 'AI-powered suggestions' },
  reminders: { label: 'Reminders', icon: Bell, description: 'Meeting reminders and notifications' }
};

const JOURNAL_DOMAINS = [
  { value: 'PERSONAL', label: 'Personal' },
  { value: 'WORK', label: 'Work' },
  { value: 'HEALTH', label: 'Health' },
  { value: 'FINANCE', label: 'Finance' },
  { value: 'LEARNING', label: 'Learning' },
  { value: 'CREATIVE', label: 'Creative' }
];

const CONTACT_CATEGORIES = [
  { value: 'WORK', label: 'Work' },
  { value: 'FRIEND', label: 'Friends' },
  { value: 'FAMILY', label: 'Family' },
  { value: 'SERVICE', label: 'Service Providers' },
  { value: 'OTHER', label: 'Other' }
];

export const DiarySettings = ({ book, bookId, onUpdate, onClose }) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [errors, setErrors] = useState({});
  const [uploadStates, setUploadStates] = useState({});

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: Settings },
    { id: 'features', label: 'Features', icon: Layout },
    { id: 'header', label: 'Header', icon: Layout },
    { id: 'design', label: 'Brand Design', icon: Palette },
    { id: 'ai', label: 'AI Settings', icon: Sparkles },
    { id: 'privacy', label: 'Privacy', icon: Shield },
    { id: 'seo', label: 'SEO & Domain', icon: Globe }
  ];

  useEffect(() => {
    if (book) {
      setFormData({
        botName: book.botName || '',
        description: book.description || '',
        logoUrl: book.logoUrl || '',
        coverImageUrl: book.coverImageUrl || '',
        headerBackgroundUrl: book.headerBackgroundUrl || '',

        primaryColor: book?.styling?.primaryColor || '#4f46e5',
        backgroundColor: book?.styling?.backgroundColor || '#0f172a',
        headerColor: book?.styling?.headerColor || '#1e293b',
        accentColor: book?.styling?.accentColor || '#8b5cf6',
        fontFamily: book?.styling?.fontFamily || 'Inter, sans-serif',
        headerBackgroundColor: book?.styling?.headerBackgroundColor || '#ffffff',
        textColor: book?.styling?.textColor || '#1e293b',
        headerRadius: book?.styling?.headerRadius || '0px',
        logoInvert: book.styling?.logoInvert ?? 0,
        cardRadius: book?.styling?.cardRadius || '12px',
        shadowLevel: book?.styling?.shadowLevel || 'medium',

        headerSettings: {
          showLogo: book.headerSettings?.showLogo ?? true,
          showTitle: book.headerSettings?.showTitle ?? true,
          showDescription: book.headerSettings?.showDescription ?? false,
          headerStyle: book.headerSettings?.headerStyle || 'modern',
          headerHeight: book.headerSettings?.headerHeight || 80,
          logoHeight: book.headerSettings?.logoHeight || 40,
          position: book.headerSettings?.position || 'sticky',
          layout: book.headerSettings?.layout || 'default',
        },
        diarySettings: {
          enabledFeatures: book.diarySettings?.enabledFeatures || ['meetings', 'journal', 'contacts', 'notes'],
          defaultView: book.diarySettings?.defaultView || 'DASHBOARD',
          defaultMeetingDuration: book.diarySettings?.defaultMeetingDuration || 60,
          enabledDomains: book.diarySettings?.enabledDomains || ['PERSONAL', 'WORK', 'HEALTH'],
          enabledContactCategories: book.diarySettings?.enabledContactCategories || ['WORK', 'FRIEND', 'FAMILY', 'SERVICE'],
          theme: book.diarySettings?.theme || 'light',
          weekStartsOn: book.diarySettings?.weekStartsOn || 'monday',
          timeFormat: book.diarySettings?.timeFormat || '24h',
          enableNotifications: book.diarySettings?.enableNotifications ?? true,
          reminderDefault: book.diarySettings?.reminderDefault || 15,
          aiContext: book.diarySettings?.aiContext || '',
          aiPersonality: book.diarySettings?.aiPersonality || 'professional',
          enableAISuggestions: book.diarySettings?.enableAISuggestions ?? true,
          geminiApiKey: book.diarySettings?.geminiApiKey || ''
        },
        privacySettings: {
          isPublic: book.privacySettings?.isPublic ?? false,
          requireAuth: book.privacySettings?.requireAuth ?? true,
          allowSharing: book.privacySettings?.allowSharing ?? false
        },

        accessLevel: book.accessLevel || 'public',
        accessPassword: book.accessPassword || '',
        requireAuth: book.requireAuth || false,
        authorizedUsers:initializeAuthorizedUsers(book.authorizedUsers),
        published: book.published !== undefined ? book.published : true,

        seoTitle: book.seoTitle || '',
        seoDescription: book.seoDescription || '',
        seoKeywords: book.seoKeywords || '',
        favicon: book.favicon || '',
        ogImage: book.ogImage || '',
        customDomain: book.customDomain || '',
        customPath: book.customPath || ''
      });
    }
  }, [book]);

  const initializeAuthorizedUsers = (authorizedUsers) => {
    // If no data, return empty object
    if (!authorizedUsers) return {};
    
    // If already in correct format (object), return as is
    if (!Array.isArray(authorizedUsers) && typeof authorizedUsers === 'object') {
      return authorizedUsers;
    }
    
    // If old format (array), migrate to new format
    if (Array.isArray(authorizedUsers)) {
      return authorizedUsers.reduce((acc, email) => {
        acc[email] = {
          email,
          permissions: {
            management: true,
            voiceRecording: true,
            uploads: true
          },
          addedAt: new Date().toISOString()
        };
        return acc;
      }, {});
    }
    
    return {};
  };

  const handleInputChange = (path, value) => {
    setFormData(prev => {
      if (path.includes('.')) {
        const keys = path.split('.');
        const newData = { ...prev };
        let current = newData;
        
        for (let i = 0; i < keys.length - 1; i++) {
          const key = keys[i];
          if (!current[key] || typeof current[key] !== 'object') {
            current[key] = {};
          } else {
            current[key] = { ...current[key] };
          }
          current = current[key];
        }
        
        current[keys[keys.length - 1]] = value;
        return newData;
      }
      
      return { ...prev, [path]: value };
    });
    setHasChanges(true);
  };

  const handleFileUpload = async (file, fileType) => {
    if (!file || !book?.id) return;

    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, [fileType]: 'File size must be less than 5MB' }));
      return;
    }

    setUploadStates(prev => ({ ...prev, [fileType]: 'uploading' }));
    setErrors(prev => ({ ...prev, [fileType]: null }));

    try {
      const path = `books/${book.id}/${fileType}`;
      const uploadResult = await firestoreService.uploadFile(file, path, book.id);
      
      const urlString = typeof uploadResult === 'string' 
        ? uploadResult 
        : uploadResult?.url || uploadResult;
      
      if (!urlString || typeof urlString !== 'string') {
        throw new Error('Invalid URL returned from upload');
      }

      const fieldMapping = {
        logo: 'logoUrl',
        coverImage: 'coverImageUrl',
        headerBackground: 'headerBackgroundUrl',
        favicon: 'favicon',
        ogImage: 'ogImage'
      };

      const fieldName = fieldMapping[fileType] || fileType;
      handleInputChange(fieldName, urlString);

      setUploadStates(prev => ({ ...prev, [fileType]: 'success' }));
      
      setTimeout(() => {
        setUploadStates(prev => ({ ...prev, [fileType]: null }));
      }, 2000);

    } catch (error) {
      console.error('Upload failed:', error);
      setUploadStates(prev => ({ ...prev, [fileType]: 'error' }));
      setErrors(prev => ({ ...prev, [fileType]: error.message || 'Upload failed' }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = {
        botName: formData.botName,
        description: formData.description,
        logoUrl: formData.logoUrl,
        coverImageUrl: formData.coverImageUrl,
        headerBackgroundUrl: formData.headerBackgroundUrl,
        favicon: formData.favicon,
        ogImage: formData.ogImage,
        
        styling: {
          primaryColor: formData.primaryColor,
          backgroundColor: formData.backgroundColor,
          headerColor: formData.headerColor,
          accentColor: formData.accentColor,
          fontFamily: formData.fontFamily,
          headerBackgroundColor: formData.headerBackgroundColor,
          textColor: formData.textColor,
          headerRadius: formData.headerRadius,
          logoInvert: formData.logoInvert,
          cardRadius: formData.cardRadius,
          shadowLevel: formData.shadowLevel
        },
        
        headerSettings: formData.headerSettings,
        diarySettings: formData.diarySettings,
        privacySettings: formData.privacySettings,

        allowAnonymousViews: book.allowAnonymousViews !== undefined ? book.allowAnonymousViews : true,
        requireLoginForContact: book.requireLoginForContact || false,
        requireLoginForInteractions: book.albumSettings?.requireLoginForInteractions || true,
        moderateSubmissions: book.moderateSubmissions || false,

        allowAnonymousViews: formData.allowAnonymousViews !== false,
        requireLoginForContact: formData.requireLoginForContact === true,
        moderateSubmissions: formData.moderateSubmissions === true,
        published: formData.published !== false,
        
        // Organization link
        organizationId: formData.organizationId || null,
        
        accessPassword: formData.accessPassword || '',
        requireAuth: formData.requireAuth === true,
        authorizedUsers: formData.authorizedUsers || {},
        
        seoTitle: formData.seoTitle,
        seoDescription: formData.seoDescription,
        seoKeywords: formData.seoKeywords,
        customDomain: formData.customDomain,
        customPath: formData.customPath,
        
        updatedAt: new Date()
      };

      await firestoreService.updateBook(bookId, updateData);
      
      if (onUpdate) {
        onUpdate(updateData);
      }
      
      setHasChanges(false);
      onClose();
    } catch (error) {
      console.error('Error saving diary settings:', error);
      setErrors({ save: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const toggleFeature = (featureKey) => {
    const currentFeatures = formData.diarySettings?.enabledFeatures || [];
    const newFeatures = currentFeatures.includes(featureKey)
      ? currentFeatures.filter(f => f !== featureKey)
      : [...currentFeatures, featureKey];
    
    handleInputChange('diarySettings.enabledFeatures', newFeatures);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return (
          <BasicInfoTab
            formData={formData}
            onInputChange={handleInputChange}
            onFileUpload={handleFileUpload}
            uploadStates={uploadStates}
            errors={errors}
            templateType="diary"
            bookId={bookId}
          />
        );

      case 'features':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Diary Features</h3>
              <p className="text-sm text-gray-500 mb-4">Enable or disable features for your diary</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(DIARY_FEATURES).map(([key, feature]) => {
                  const Icon = feature.icon;
                  const isEnabled = formData.diarySettings?.enabledFeatures?.includes(key);
                  
                  return (
                    <div
                      key={key}
                      onClick={() => toggleFeature(key)}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        isEnabled 
                          ? 'border-indigo-500 bg-indigo-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${isEnabled ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-500'}`}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{feature.label}</h4>
                          <p className="text-sm text-gray-500">{feature.description}</p>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          isEnabled ? 'border-indigo-500 bg-indigo-500' : 'border-gray-300'
                        }`}>
                          {isEnabled && <div className="w-2 h-2 bg-white rounded-full" />}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Default Settings</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Default View</label>
                  <select
                    value={formData.diarySettings?.defaultView || 'DASHBOARD'}
                    onChange={(e) => handleInputChange('diarySettings.defaultView', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="DASHBOARD">Dashboard</option>
                    <option value="CALENDAR">Calendar</option>
                    <option value="MEETINGS">Meetings</option>
                    <option value="JOURNAL">Journal</option>
                    <option value="CONTACTS">Contacts</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Default Meeting Duration (minutes)</label>
                  <input
                    type="number"
                    value={formData.diarySettings?.defaultMeetingDuration || 60}
                    onChange={(e) => handleInputChange('diarySettings.defaultMeetingDuration', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    min="15"
                    step="15"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Week Starts On</label>
                  <select
                    value={formData.diarySettings?.weekStartsOn || 'monday'}
                    onChange={(e) => handleInputChange('diarySettings.weekStartsOn', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="sunday">Sunday</option>
                    <option value="monday">Monday</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Time Format</label>
                  <select
                    value={formData.diarySettings?.timeFormat || '24h'}
                    onChange={(e) => handleInputChange('diarySettings.timeFormat', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="12h">12-hour (AM/PM)</option>
                    <option value="24h">24-hour</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Journal Domains</h3>
              <p className="text-sm text-gray-500 mb-4">Select which journal categories to enable</p>
              
              <div className="flex flex-wrap gap-2">
                {JOURNAL_DOMAINS.map(domain => {
                  const isEnabled = formData.diarySettings?.enabledDomains?.includes(domain.value);
                  return (
                    <button
                      key={domain.value}
                      type="button"
                      onClick={() => {
                        const current = formData.diarySettings?.enabledDomains || [];
                        const updated = isEnabled
                          ? current.filter(d => d !== domain.value)
                          : [...current, domain.value];
                        handleInputChange('diarySettings.enabledDomains', updated);
                      }}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                        isEnabled
                          ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-500'
                          : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
                      }`}
                    >
                      {domain.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Categories</h3>
              <p className="text-sm text-gray-500 mb-4">Select which contact categories to enable</p>
              
              <div className="flex flex-wrap gap-2">
                {CONTACT_CATEGORIES.map(category => {
                  const isEnabled = formData.diarySettings?.enabledContactCategories?.includes(category.value);
                  return (
                    <button
                      key={category.value}
                      type="button"
                      onClick={() => {
                        const current = formData.diarySettings?.enabledContactCategories || [];
                        const updated = isEnabled
                          ? current.filter(c => c !== category.value)
                          : [...current, category.value];
                        handleInputChange('diarySettings.enabledContactCategories', updated);
                      }}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                        isEnabled
                          ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-500'
                          : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
                      }`}
                    >
                      {category.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        );

      case 'design':
        return (
            <DesignBrandingSection
              formData={formData}
              onInputChange={handleInputChange}
              templateType="diary"
            />
        );

      case 'header': 
        return (     
            <HeaderSettings
              formData={formData}
              onInputChange={handleInputChange}
              onImageUpload={handleFileUpload}
              uploadStates={uploadStates}
              bookId={bookId}
            />
        );

      case 'ai':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">AI Assistant Settings</h3>
              
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg mb-4">
                <div>
                  <h4 className="font-medium text-gray-900">Enable AI Suggestions</h4>
                  <p className="text-sm text-gray-500">Get AI-powered meeting agenda and note suggestions</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleInputChange('diarySettings.enableAISuggestions', !formData.diarySettings?.enableAISuggestions)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    formData.diarySettings?.enableAISuggestions ? 'bg-indigo-600' : 'bg-gray-300'
                  }`}
                >
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                    formData.diarySettings?.enableAISuggestions ? 'translate-x-7' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Gemini API Key</label>
                  <input
                    type="password"
                    value={formData.diarySettings?.geminiApiKey || ''}
                    onChange={(e) => handleInputChange('diarySettings.geminiApiKey', e.target.value)}
                    placeholder="Enter your Gemini API key"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Required for AI features. Get your key from Google AI Studio.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">AI Personality</label>
                  <select
                    value={formData.diarySettings?.aiPersonality || 'professional'}
                    onChange={(e) => handleInputChange('diarySettings.aiPersonality', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="professional">Professional</option>
                    <option value="friendly">Friendly</option>
                    <option value="concise">Concise</option>
                    <option value="detailed">Detailed</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">AI Context</label>
                  <textarea
                    value={formData.diarySettings?.aiContext || ''}
                    onChange={(e) => handleInputChange('diarySettings.aiContext', e.target.value)}
                    placeholder="Provide context about yourself, your work, or preferences to help the AI give better suggestions..."
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This context helps personalize AI suggestions for agendas, notes, and meeting summaries.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'privacy':
        return (
          <PrivacySection
            bookId={bookId}
            formData={formData}
            onInputChange={handleInputChange}
            templateType="diary"
          />
        );

      case 'seo':
        return (
          <SEOSection
            formData={formData}
            onInputChange={handleInputChange}
            onImageUpload={handleFileUpload}
            uploadStates={uploadStates}
            errors={errors}
            bookId={bookId}
          />
        );

      default:
        return null;
    }
  };

  return (
    <SettingsModal
      isOpen={true}
      onClose={onClose}
      title="Diary Settings"
      subtitle="Configure your personal diary and meeting workspace"
      onSave={handleSave}
      saving={saving}
      hasChanges={hasChanges}
    >
      <div className="flex h-full overflow-hidden">
        <TabNavigation
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      </div>
        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="p-6">
            {renderTabContent()}
          </div>       
        </div>
      
    </SettingsModal>
  );
};